<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText class="resource-title-list-title" :field="fields?.title" tag="h2" />
      <JssRichText class="resource-title-list-desc" :field="fields?.subtitle" />
      <div
        class="resource-title-list-content"
        :style="{ '--cols': severalPerLine }"
        v-if="fields?.list?.length > 0"
      >
        <div
          v-for="(item, index) in fields?.list"
          :key="index"
          class="resource-title-list-content-item"
        >
          <AdaptiveImage
            class="resource-title-list-content-item-bg"
            :field="item?.fields?.backgroundImage"
            :isBackground="true"
            :lazy-load="false"
            :enableScrollDetection="false"
            :show="true"
          >
            <JssRichText
              class="resource-title-list-content-item-bg-text"
              :field="item?.fields?.bgText"
            />
          </AdaptiveImage>
          <div class="resource-title-list-content-item-body">
            <div>
              <div class="resource-title-list-content-item-body-container">
                <JssRichText
                  class="resource-title-list-content-item-body-container-tag"
                  :field="item?.fields?.tag"
                />
                <JssRichText
                  class="resource-title-list-content-item-body-container-title"
                  :field="item?.fields?.title"
                  tag="h3"
                />
              </div>
              <JssRichText
                class="resource-title-list-content-item-body-desc"
                :field="item?.fields?.desc"
              />
            </div>
            <div class="resource-title-list-content-item-body-footer">
              <div class="resource-title-list-content-item-body-footer-file">
                <span>{{ item?.fields?.button?.fields?.file?.value?.mimeType.toUpperCase() }}</span>
                <span v-if="item?.fields?.button?.fields?.file?.value?.src">·</span>
                <span>{{ formatFileSize(item?.fields?.button?.fields?.file?.value?.size) }}</span>
              </div>
              <SiteButton
                v-bind="item?.fields?.button"
                :open-link="!fields?.openModal?.value"
                :open-download="!fields?.openModal?.value"
                @click="(event) => onButtonClick(event, item, fields?.openModal?.value)"
              />
            </div>
          </div>
        </div>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { computed, ref, inject, onMounted } from 'vue';
import BUS_EVENTS from '@/utils/bus-events';
import { setLeadFormSubmitted, isLeadFormSubmitted } from '@/utils/form-utils';
import utils from '@/utils';
export default {
  name: 'ResourceTitleList',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
  },
  setup(props) {
    const bus = inject('$bus');
    let isSubmitted = ref(false);

    const isEmptyContent = computed(() => {
      return !props.fields?.list || props.fields.list.length === 0;
    });

    const severalPerLine = computed(() => {
      const val = parseInt(props.fields?.severalPerLine?.fields?.phrase?.value, 10);
      return val > 0 ? val : 2;
    });

    const rootClass = computed(() =>
      utils.overall.baseComClass(props.fields, 'resource-title-list', {
        'media-mode': isEmptyContent.value,
      })
    );

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return result;
    });

    const methods = {
      download(item) {
        const url = item?.fields?.file?.value?.src;
        if (!url) return;

        const fileName = item?.fields?.file?.value?.name || 'download';
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      performButtonAction(item) {
        const buttonFields = item?.fields?.button?.fields;
        const href = buttonFields?.link?.value?.href;
        const fileSrc = buttonFields?.file?.value?.src;
        const modeview = buttonFields?.modeview?.value;
        if (href) {
          window.open(href, '_blank');
        } else if (fileSrc) {
          if (modeview) {
            window.open(fileSrc, '_blank');
          } else {
            methods.download(item?.fields?.button);
          }
        }
      },
      onButtonClick(event, item, openModal) {
        event.preventDefault();
        if (!openModal) {
          return;
        }
        if (isSubmitted.value) {
          methods.performButtonAction(item);
          return;
        }
        bus?.emit(BUS_EVENTS.FORM_OPEN, {
          onSuccess: () => {
            setLeadFormSubmitted('resourceTitle');
            isSubmitted.value = true;
            methods.performButtonAction(item);
          },
        });
      },
    };

    onMounted(() => {
      if (isLeadFormSubmitted('resourceTitle')) {
        isSubmitted.value = true;
      } else {
        isSubmitted.value = false;
      }
    });

    return {
      rootClass,
      rootStyle,
      severalPerLine,
      ...methods,
    };
  },
  methods: {
    formatFileSize(size) {
      if (!size) return '';
      const sizeNum = parseInt(size, 10);
      if (isNaN(sizeNum)) return size;
      if (sizeNum >= 1024) {
        return (sizeNum / 1024).toFixed(1) + 'M';
      }
      return sizeNum + 'KB';
    },
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.resource-title-list {
  position: relative;
  width: 100%;
  padding: 40px 0;

  &-title {
    text-align: center;
  }

  &-desc {
    font-size: 14px;
    line-height: 1.6;
    color: $black;
    margin-top: 8px;
  }

  &-content {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    margin-top: 24px;

    &-item {
      display: flex;
      width: 100%;
      gap: 16px;
      background: #f6faf9;
      padding: 16px;
      border-radius: 6px;

      &-bg {
        width: 80px;
        height: 107px;
        position: relative;
        flex-shrink: 0;

        &-text {
          font-size: 12px;
          color: $black;
          text-transform: uppercase;
          line-height: 1.1;
          position: relative;
          z-index: 1;
          margin: 6px 0 0 6px;
        }
      }

      &-body {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        &-container {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          gap: 8px;

          &-tag {
            padding: 3px 6px;
            border-radius: 3px;
            font-size: 12px;
            color: #e9550d;
          }

          &-title {
            font-size: 16px;
            font-weight: 600;
            color: $black;
            line-height: 1.4;
            @include line-clamp(2);
          }
        }

        &-desc {
          font-size: 13px;
          color: #666666;
          line-height: 1.5;
          margin-top: 8px;
          @include line-clamp(3);
        }

        &-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 10px;

          &-file {
            font-size: 12px;
            color: #999999;
          }

          &-download {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 12px;
            color: #4a9b6a;
            text-decoration: none;
            cursor: pointer;

            &:hover {
              text-decoration: none;
            }

            &-text {
              color: $blue-night;
            }
          }
        }
      }
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-desc {
      font-size: 18px;
    }

    &-content {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
      gap: 26px;
      margin-top: 40px;

      &-item {
        display: flex;
        width: calc((100% - (var(--cols) - 1) * 26px) / var(--cols));
        gap: 24px;

        &-bg {
          width: 112px;
          height: 149px;
          position: relative;
          display: flex;
          align-items: flex-start;
          justify-content: flex-start;

          &-text {
            font-size: 16px;
            color: $black;
            text-transform: uppercase;
            line-height: 1.1;
            position: relative;
            z-index: 1;
            margin: 11px 0 0 9px;
          }
        }

        &-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;

          &-container {
            display: flex;
            gap: 8px;

            &-tag {
              background: rgba(#ea9064, 0.2);
              padding: 3px 6px;
              border-radius: 3px;
              font-size: 12px;
              color: #e9550d;
            }

            &-title {
              font-size: 18px;
              height: 48px;
            }
          }

          &-desc {
            font-size: 14px;
            color: $black;
            line-height: 1.6;
          }

          &-footer {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-top: 12px;
            margin-top: 0;

            &-file {
              font-size: 14px;
            }

            &-download {
              display: flex;
              align-items: center;
              gap: 6px;
              font-size: 13px;
              color: #4a9b6a;
              text-decoration: none;
              cursor: pointer;

              &:hover {
                text-decoration: none;
              }

              &-text {
                color: $blue-night;
              }
            }
          }
        }
      }
    }
  }
}
</style>
